import React, { useRef, useState } from 'react';
import check from '../../assets/check.png';
import drag from "../../assets/drag.svg"
import chevron from "../../assets/chevron.svg"
import { useCachedFxRange } from "../../hooks/useCachedData";
import { getRangeStatus, getStatusColor, getStatusLabel, convertFxRange } from '../../util/GenericUtil';
import BoxPlot from '../Visualizations/BoxPlot';
import LinePlot from '../Visualizations/LinePlot';
import DetailedTestChart from '../DetailedTestChart/DetailedTestChart';
import AutocompleteDropdown from '../AutocompleteDropdown/AutocompleteDropdown';
import { getTestNames, getTestData, isRecognizedTest } from '../../data/defaultTestData';
import { getMarkerDescription } from '../../data/markerDescriptions';
import { useScreen } from "../../context/ScreenProvider";
import MarkerDetailsModal from '../MarkerDetailsModal/MarkerDetailsModal';

function TestChartEntry(props) {
    const [renderSuccess, setRenderSuccess] = useState(false)

    // dragging functionality
    const [dragging, setDragging] = useState(false)
    const [offset, setOffset] = useState(0)
    const [closestIndex, setClosestIndex] = useState(props.index)
    const startPos = useRef(null)

    // mobile responsive effects
    const { isMobile } = useScreen()
    const [tempExpanded, setTempExpanded] = useState(false)
    const [detailInfoExpanded, setDetailInfoExpanded] = useState(false)

    // marker details modal
    const [showMarkerModal, setShowMarkerModal] = useState(false)

    const selectCurrentEntry = (select) => {
        if (select) {
            props.setNewEntrySelected([...props.newEntrySelected, props.index])
        } else {
            props.setNewEntrySelected(props.newEntrySelected.filter(e => e !== props.index))
        }
    }
    
    const getClosestIndexForDrop = (y) => {
        let closestDistance = Infinity
        let closestIndex = props.index
        for (let i = 0; i < props.entryRefs.current.length; i++) {
            const rect = props.entryRefs.current[i].getBoundingClientRect()

            const topDistance = props.index == i ? Math.abs(y - startPos.current) : Math.abs(y - rect.top)
            const bottomDistance =  props.index == i ? Math.abs(y - startPos.current)  : Math.abs(y - rect.bottom)

            if (topDistance < closestDistance || bottomDistance < closestDistance) {
                closestDistance = Math.min(topDistance, bottomDistance)
                closestIndex = i
            }
        }

        return closestIndex
    }

    const handleDragStart = (startY, og_event) => {
        startPos.current = startY
        props.dragJustEnded.current = true
        setDragging(true);

        const handleMove = (e) => {
            e.preventDefault()
            if (startPos.current !== null) {
                setOffset(e.touches ? e.touches[0].clientY - startPos.current: e.clientY - startPos.current)
            }

            const closestIndex = getClosestIndexForDrop(e.touches ? e.touches[0].clientY : e.clientY)
            props.entryRefs.current.forEach((ele, i) => {
                if (i != props.index && i == closestIndex) {
                    ele.classList.add("activeDrop")

                    ele.querySelector(".dropIndicator").style.bottom = closestIndex > props.index ? "-7px" : ""
                    ele.querySelector(".dropIndicator").style.top = closestIndex > props.index ? "" : "-7px"
                } else {
                    ele.classList.remove("activeDrop")
                }
            })
        };

        const handleEnd = (e) => {
            props.entryRefs.current.forEach((ele, i) => {
                ele.classList.remove("activeDrop")
            })
            
            const closestIndex = getClosestIndexForDrop(e.changedTouches ? e.changedTouches[0].clientY : e.clientY)

            setDragging(false);
            setOffset(0)
            startPos.current = null
            
            let shifted = [...props.newTest.data.body]
            let switchItem = shifted.splice(props.index, 1)[0]
            shifted.splice(closestIndex, 0, switchItem)

            props.setNewTest({
                ...props.newTest,
                data: {
                    ...props.newTest.data,
                    body: shifted
                }
            })

            setTimeout(() => props.dragJustEnded.current = false, 50)

            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleEnd);
            window.removeEventListener("touchmove", handleMove, { passive: false });
            window.removeEventListener("touchend", handleEnd);
        };

        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleEnd);
        window.addEventListener("touchmove", handleMove, { passive: false });
        window.addEventListener("touchend", handleEnd);
    }

    // TODO update when we include patient.json also see line 203
    const rawFxRange = useCachedFxRange().getFxRange(props.test.alias, "male", false)
    const fxRange = convertFxRange(rawFxRange, props.test.alias, props.test.units)
    // For aggregate view, use the most recent result (last in array); otherwise use the single result
    const resultValue = props.aggregate && props.test.results && props.test.results.length > 0
        ? (typeof props.test.results[props.test.results.length - 1] === 'object'
            ? props.test.results[props.test.results.length - 1].result
            : props.test.results[props.test.results.length - 1])
        : props.test.result;
    const rangeStatus = getRangeStatus(resultValue, props.test.lab_low, props.test.lab_high, fxRange?.fx_low ?? null, fxRange?.fx_high ?? null)
    const markerInfo = getMarkerDescription(props.test.alias || props.test.test_name)

    if (!props.temp && props.tags.length && !props.tags.includes(rangeStatus)) {
        return ""
    }

    if (props.temp) {
        return (
            <div className={`testChartEntry temp ${props.newEntrySelected.includes(props.index) ? "selected" : ""} ${dragging ? "dragging" : ""}`} onClick={() => {
                if (isMobile && !props.dragJustEnded.current) {
                    setTempExpanded(!tempExpanded)
                }
                }} style={offset != 0 ? {
                    transform: `translateY(${offset}px)`
                } : {}}>
                <div className="dropIndicator"></div>
                <div className="info">
                    {props.selectMode ?
                    <div className="selectContainer" onClick={() => { props.newEntrySelected.includes(props.index) ? selectCurrentEntry(false) : selectCurrentEntry(true) }}>
                        <div className="customCheckbox">
                            {props.newEntrySelected.includes(props.index) ? <img src={check} /> : ""}
                        </div>
                    </div> : ""}
                    <div className="drag" 
                        onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleDragStart(e.clientY, e)
                            }}
                        onTouchStart={(e) => {
                            handleDragStart(e.touches[0].clientY, e)
                            }}>
                        <img src={drag} />
                    </div>
                    {isMobile ? 
                    <>
                        <p>{props.newTest.data.body[props.index].test_name}</p>
                        <img className="chevron" src={chevron} />
                    </> :
                        <InputFields index={props.index} newTest={props.newTest} setNewTest={props.setNewTest} showError={props.showError} showWarning={props.showWarning} />
                    }
                </div>
                {isMobile ?
                <div className={`info_detailed ${tempExpanded ? "expanded" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                }}>
                    <InputFields index={props.index} newTest={props.newTest} setNewTest={props.setNewTest} showError={props.showError} />
                </div> : ""}
            </div>
        );
    } else if (props.aggregate) {
        return (
            <div
                className={`testChartEntry ${props.isSelected ? 'selected' : ''}`}
                onClick={() => props.onTestClick && props.onTestClick()}
                style={{ cursor: props.onTestClick ? 'pointer' : 'default' }}
            >
                <div className="rowContent">
                    <div className="topWrapper">
                        <p className="name">{props.test.test_name}</p>
                        <p className={`status ${rangeStatus}`} placeholder={"test"}>{getStatusLabel(rangeStatus)}</p>
                        {/* using most recent result value (last in array) */}
                        <p className="value">{resultValue} {props.test.units}</p>
                    </div>
                    <div className="bottomWrapper">
                        {props.isSelected ? (
                            <div className="range">
                                {fxRange && (
                                    <span className="rangeLabel functional">Ideal: {props.test.lab_low != null ? Math.max(fxRange.fx_low, props.test.lab_low) : fxRange.fx_low} - {props.test.lab_high != null ? Math.min(fxRange.fx_high, props.test.lab_high) : fxRange.fx_high}</span>
                                )}
                                {(props.test.lab_low != null || props.test.lab_high != null) && (
                                    <span className="rangeLabel lab">Lab: {props.test.lab_low != null && props.test.lab_high != null
                                        ? `${props.test.lab_low} - ${props.test.lab_high}`
                                        : props.test.lab_high != null ? `<${props.test.lab_high}` : `>${props.test.lab_low}`
                                    }</span>
                                )}
                            </div>
                        ) : (
                            <div className="plot">
                                {props.test.results.length > 0 ?
                                    <LinePlot test={props.test} fxRange={fxRange} labLow={props.test.lab_low} labHigh={props.test.lab_high} />
                                    : <p>No data points</p>}
                            </div>
                        )}
                    </div>
                </div>
                <div className="accordionContent">
                    {props.isSelected && props.test.results && props.test.results.length > 0 && (
                        <DetailedTestChart
                            test={props.test}
                            fxRange={fxRange}
                            labLow={props.test.lab_low}
                            labHigh={props.test.lab_high}
                        />
                    )}
                    {props.isSelected && markerInfo && (
                        <div className="marker-info">
                            <p className="marker-info-description">{markerInfo.description}</p>
                            {(rangeStatus === 'high' || rangeStatus === 'elevated') && markerInfo.high && (
                                <p className="marker-info-blurb" style={{ borderLeft: `2px solid ${getStatusColor(rangeStatus)}` }}>{markerInfo.high}</p>
                            )}
                            {(rangeStatus === 'low' || rangeStatus === 'diminished') && markerInfo.low && (
                                <p className="marker-info-blurb" style={{ borderLeft: `2px solid ${getStatusColor(rangeStatus)}` }}>{markerInfo.low}</p>
                            )}
                            <button className="see-more-link" onClick={(e) => {
                                e.stopPropagation();
                                setShowMarkerModal(true);
                            }}>
                                ...More info
                            </button>
                        </div>
                    )}
                </div>
                {showMarkerModal && (
                    <MarkerDetailsModal
                        markerName={props.test.alias || props.test.test_name}
                        markerInfo={markerInfo}
                        onClose={() => setShowMarkerModal(false)}
                    />
                )}
            </div>
        );
    } else {
        return (
            <div
                className={`testChartEntry detailed ${detailInfoExpanded ? 'selected' : ''}`}
                onClick={() => markerInfo && setDetailInfoExpanded(!detailInfoExpanded)}
                style={{ cursor: markerInfo ? 'pointer' : 'default' }}
            >
                <div className="topWrapper">
                    <p className="name">{props.test.test_name}</p>
                    <p className={`status ${rangeStatus}`} placeholder={"test"}>{getStatusLabel(rangeStatus)}</p>
                    <p className="value">{props.test.result} {props.test.units}</p>
                </div>
                <div className="bottomWrapper">
                    <div className="plot">
                        <BoxPlot setRenderSuccess={setRenderSuccess} llow={props.test.lab_low} lhigh={props.test.lab_high} fxlow={fxRange?.fx_low ?? null} fxhigh={fxRange?.fx_high ?? null} result={props.test.result} />
                    </div>
                </div>
                <div className="accordionContent">
                    {detailInfoExpanded && markerInfo && (
                        <div className="marker-info">
                            <p className="marker-info-description">{markerInfo.description}</p>
                            {(rangeStatus === 'high' || rangeStatus === 'elevated') && markerInfo.high && (
                                <p className="marker-info-blurb" style={{ borderLeft: `2px solid ${getStatusColor(rangeStatus)}` }}>{markerInfo.high}</p>
                            )}
                            {(rangeStatus === 'low' || rangeStatus === 'diminished') && markerInfo.low && (
                                <p className="marker-info-blurb" style={{ borderLeft: `2px solid ${getStatusColor(rangeStatus)}` }}>{markerInfo.low}</p>
                            )}
                            <button className="see-more-link" onClick={(e) => {
                                e.stopPropagation();
                                setShowMarkerModal(true);
                            }}>
                                ...More info
                            </button>
                        </div>
                    )}
                </div>
                {showMarkerModal && (
                    <MarkerDetailsModal
                        markerName={props.test.alias || props.test.test_name}
                        markerInfo={markerInfo}
                        onClose={() => setShowMarkerModal(false)}
                    />
                )}
            </div>
        );
    }
}

function InputFields ({ index, setNewTest, newTest, showError, showWarning }) {
    const [nameFocus, setNameFocus] = useState(false)
    const [resultFocus, setResultFocus] = useState(false)
    const [unitsFocus, setUnitsFocus] = useState(false)
    const [labLowFocus, setLabLowFocus] = useState(false)
    const [labHighFocus, setLabHighFocus] = useState(false)

    return (
        <>
            <InputField name={"Test Name"} key={"test_name"} index={index} property={"test_name"} setFocus={setNameFocus} focus={nameFocus} setNewTest={setNewTest} newTest={newTest} value={newTest.data.body[index].test_name} showError={showError} showWarning={showWarning} />
            <InputField name={"Result"} key={"test_result"} index={index} property={"result"} setFocus={setResultFocus} focus={resultFocus} setNewTest={setNewTest} newTest={newTest} value={newTest.data.body[index].result} showError={showError} showWarning={showWarning} />
            <InputField name={"Units"} key={"test_units"} index={index} property={"units"} setFocus={setUnitsFocus} focus={unitsFocus} setNewTest={setNewTest} newTest={newTest} value={newTest.data.body[index].units} showError={showError} showWarning={showWarning} />
            <InputField name={"Lab Low"} key={"test_low"} index={index} property={"lab_low"} setFocus={setLabLowFocus} focus={labLowFocus} setNewTest={setNewTest} newTest={newTest} value={newTest.data.body[index].lab_low} showError={showError} showWarning={showWarning} />
            <InputField name={"Lab High"} key={"test_high"} index={index} property={"lab_high"} setFocus={setLabHighFocus} focus={labHighFocus} setNewTest={setNewTest} newTest={newTest} value={newTest.data.body[index].lab_high} showError={showError} showWarning={showWarning} />
        </>
    );
}

function InputField(props) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    const isTestNameField = props.property === "test_name";
    const isAutoPopulatedField = ["units", "lab_low", "lab_high"].includes(props.property);

    // Check if this field is currently auto-populated
    const currentRow = props.newTest.data.body[props.index];
    const isCurrentlyAutoPopulated = isAutoPopulatedField &&
        currentRow._autoPopulated &&
        currentRow._autoPopulated[props.property] === true;

    // Check if this field has been confirmed by user
    const isConfirmed = currentRow._confirmed && currentRow._confirmed[props.property] === true;

    // Auto-populate related fields when a recognized test is selected
    const autoPopulateFields = (testName) => {
        if (!isRecognizedTest(testName)) return;

        const testData = getTestData(testName);
        if (!testData) return;

        const currentRow = props.newTest.data.body[props.index];
        const updatedRow = {
            ...currentRow,
            units: testData.units,
            lab_low: testData.lab_low,
            lab_high: testData.lab_high,
            // Track which fields are auto-populated
            _autoPopulated: {
                ...currentRow._autoPopulated,
                units: true,
                lab_low: true,
                lab_high: true
            }
        };

        props.setNewTest({
            ...props.newTest,
            data: {
                ...props.newTest.data,
                body: [
                    ...props.newTest.data.body.slice(0, props.index),
                    updatedRow,
                    ...props.newTest.data.body.slice(props.index + 1)
                ]
            }
        });
    };

    // Get existing test names from the form (excluding current row)
    const getExistingTestNames = () => {
        return props.newTest.data.body
            .map((test, index) => ({ name: test.test_name, index }))
            .filter(({ name, index }) => name && index !== props.index)
            .map(({ name }) => name.toLowerCase().trim());
    };

    // Check if a test name is a duplicate
    const isDuplicateTestName = (testName) => {
        if (!testName) return false;
        const existingNames = getExistingTestNames();
        return existingNames.includes(testName.toLowerCase().trim());
    };

    // Show duplicate error popup
    const showDuplicateError = (testName) => {
        const message = `The test "${testName}" has already been added to this form. Please choose a different test name.`;
        alert(message);
    };

    // Filter suggestions based on input value and exclude duplicates
    const filterSuggestions = (input) => {
        if (!input || !isTestNameField) return [];

        const testNames = getTestNames();
        const existingNames = getExistingTestNames();
        
        const filtered = testNames.filter(testName => {
            const matchesInput = testName.toLowerCase().includes(input.toLowerCase());
            const isNotDuplicate = !existingNames.includes(testName.toLowerCase());
            return matchesInput && isNotDuplicate;
        });

        // Sort by relevance - exact matches first, then starts with, then contains
        return filtered.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();
            const inputLower = input.toLowerCase();

            if (aLower === inputLower) return -1;
            if (bLower === inputLower) return 1;
            if (aLower.startsWith(inputLower) && !bLower.startsWith(inputLower)) return -1;
            if (bLower.startsWith(inputLower) && !aLower.startsWith(inputLower)) return 1;
            return a.localeCompare(b);
        });
    };

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;

        const newBody = [...props.newTest.data.body]
        newBody[props.index] = {
            ...newBody[props.index],
            [props.property]: value
        };

        props.setNewTest({
            ...props.newTest,
            data: {
                ...props.newTest.data,
                body: newBody
            }
        })

        // Handle autocomplete for test_name field
        if (isTestNameField) {
            const suggestions = filterSuggestions(value);
            setFilteredSuggestions(suggestions);
            setShowDropdown(suggestions.length > 0 && value.length > 0);
            setSelectedSuggestionIndex(-1);
        }
    };

    // Handle input focus
    const handleInputFocus = () => {
        props.setFocus(true);

        if (isTestNameField && props.value) {
            const suggestions = filterSuggestions(props.value);
            setFilteredSuggestions(suggestions);
            setShowDropdown(suggestions.length > 0);
        }
    };

    // Handle input click for auto-populated fields
    const handleInputClick = () => {
        if (isCurrentlyAutoPopulated && !isConfirmed && inputRef.current) {
            // Select all text in auto-populated field
            inputRef.current.select();
        }
    };

    // Confirm auto-populated field value
    const confirmField = () => {
        if (isCurrentlyAutoPopulated && !isConfirmed) {
            const currentRow = props.newTest.data.body[props.index];
            const updatedRow = {
                ...currentRow,
                _confirmed: {
                    ...currentRow._confirmed,
                    [props.property]: true
                }
            };

            props.setNewTest({
                ...props.newTest,
                data: {
                    ...props.newTest.data,
                    body: [
                        ...props.newTest.data.body.slice(0, props.index),
                        updatedRow,
                        ...props.newTest.data.body.slice(props.index + 1)
                    ]
                }
            });
        }
    };

    // Handle input blur
    const handleInputBlur = (e) => {
        props.setFocus(false);

        // Check if manually typed test name matches a recognized test
        const currentValue = e.target.value;
        if (isTestNameField && currentValue) {
            // Check for duplicates first
            if (isDuplicateTestName(currentValue)) {
                showDuplicateError(currentValue);
                // Clear the field since it's a duplicate
                props.setNewTest({
                    ...props.newTest,
                    data: {
                        ...props.newTest.data,
                        body: [
                            ...props.newTest.data.body.slice(0, props.index),
                            { ...props.newTest.data.body[props.index], test_name: "" },
                            ...props.newTest.data.body.slice(props.index + 1)
                        ]
                    }
                });
                
                // Refocus the input field after the alert is dismissed
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                }, 100);
                
                return;
            }

            // If not a duplicate and is a recognized test, auto-populate
            if (isRecognizedTest(currentValue)) {
                autoPopulateFields(currentValue);
                // Move cursor to result field when manually typed test name is recognized
                moveCursorToResultField();
            }
        }

        // Confirm auto-populated field when clicking away after selection
        if (isCurrentlyAutoPopulated && !isConfirmed) {
            confirmField();
        }

        // Don't immediately hide dropdown to allow for clicks
        setTimeout(() => {
            setShowDropdown(false);
            setSelectedSuggestionIndex(-1);
        }, 150);
    };

    // Move cursor to result field with proper error handling
    const moveCursorToResultField = () => {
        setTimeout(() => {
            try {
                // Find the result input in the same row
                const resultInput = document.querySelector(`input[data-property="result"][data-index="${props.index}"]`);
                
                // Check if result field exists and is not disabled
                if (resultInput && !resultInput.disabled && !resultInput.readOnly) {
                    resultInput.focus();
                } else {
                    // If result field doesn't exist or is disabled, try to focus the next available field
                    const nextAvailableField = findNextAvailableField();
                    if (nextAvailableField) {
                        nextAvailableField.focus();
                    }
                }
            } catch (error) {
                // Silently handle any focus errors - don't break the user experience
                console.warn('Could not move cursor to result field:', error);
            }
        }, 100);
    };

    // Find the next available field that can receive focus
    const findNextAvailableField = () => {
        const fieldOrder = ['result', 'units', 'lab_low', 'lab_high'];
        
        for (const fieldName of fieldOrder) {
            const field = document.querySelector(`input[data-property="${fieldName}"][data-index="${props.index}"]`);
            if (field && !field.disabled && !field.readOnly) {
                return field;
            }
        }
        
        return null;
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (testName) => {
        // Check for duplicates before selecting
        if (isDuplicateTestName(testName)) {
            showDuplicateError(testName);
            setShowDropdown(false);
            setSelectedSuggestionIndex(-1);
            
            // Refocus the input field after the alert is dismissed
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
            
            return;
        }

        const currentRow = props.newTest.data.body[props.index];
        let updatedRow = {
            ...currentRow,
            test_name: testName
        };

        // Auto-populate related fields for recognized tests
        if (isRecognizedTest(testName)) {
            const testData = getTestData(testName);
            if (testData) {
                updatedRow = {
                    ...updatedRow,
                    units: testData.units,
                    lab_low: testData.lab_low,
                    lab_high: testData.lab_high,
                    // Track which fields are auto-populated
                    _autoPopulated: {
                        ...currentRow._autoPopulated,
                        units: true,
                        lab_low: true,
                        lab_high: true
                    }
                };
            }
        }

        // Update state with both test name and auto-populated fields in one update
        props.setNewTest({
            ...props.newTest,
            data: {
                ...props.newTest.data,
                body: [
                    ...props.newTest.data.body.slice(0, props.index),
                    updatedRow,
                    ...props.newTest.data.body.slice(props.index + 1)
                ]
            }
        });

        setShowDropdown(false);
        setSelectedSuggestionIndex(-1);

        // Move cursor to result field with proper error handling
        moveCursorToResultField();
    };

    // Move cursor to Add Row button
    const moveCursorToAddRowButton = () => {
        setTimeout(() => {
            try {
                // Find the Add Row button
                const addRowButton = document.querySelector('button.addRow');
                
                // Check if Add Row button exists and is not disabled
                if (addRowButton && !addRowButton.disabled) {
                    addRowButton.focus();
                } else {
                    // Handle edge case where Add Row button might not be available
                    console.warn('Add Row button not available for focus');
                }
            } catch (error) {
                // Silently handle any focus errors - don't break the user experience
                console.warn('Could not move cursor to Add Row button:', error);
            }
        }, 100);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        // Handle Enter key for auto-populated field confirmation (units, lab_low, lab_high)
        if (e.key === 'Enter' && isAutoPopulatedField && isCurrentlyAutoPopulated && !isConfirmed) {
            e.preventDefault();
            confirmField();
            return;
        }

        // Handle dropdown navigation for test_name field
        if (showDropdown && isTestNameField) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedSuggestionIndex(prev =>
                        prev < filteredSuggestions.length - 1 ? prev + 1 : prev                      );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < filteredSuggestions.length) {
                        handleSuggestionSelect(filteredSuggestions[selectedSuggestionIndex]);
                    }
                    break;
                case 'Escape':
                    setShowDropdown(false);
                    setSelectedSuggestionIndex(-1);
                    break;
            }
        } else if (e.key === 'Enter') {
            // Handle Enter key navigation to Add Row button for all input fields
            e.preventDefault();
            moveCursorToAddRowButton();
        }
    };

    // Handle dropdown close
    const handleDropdownClose = () => {
        setShowDropdown(false);
        setSelectedSuggestionIndex(-1);
    };

    // Calculate dropdown position
    const getDropdownPosition = () => {
        if (!containerRef.current) return {};

        const rect = containerRef.current.getBoundingClientRect();
        return {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000
        };
    };

    return (
        <div
            ref={containerRef}
            className={`inputContainer ${props.showError && !props.value && props.property !== 'units' && props.property !== 'lab_low' && props.property !== 'lab_high' ? "error" : ""}${props.showWarning && !props.value && (props.property === 'units' || props.property === 'lab_low' || props.property === 'lab_high') ? " warning" : ""}`}
            style={{ position: 'relative' }}
        >
            <p className={`${props.value || props.focus ? "topLeftLabel" : ""}`}>{props.name}</p>
            <input
                ref={inputRef}
                type="text"
                data-property={props.property}
                data-index={props.index}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onClick={handleInputClick}
                value={props.value || ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                style={{
                    opacity: (isCurrentlyAutoPopulated && !isConfirmed) ? 0.5 : 1
                }}
            />

            {isTestNameField && showDropdown && (
                <AutocompleteDropdown
                    suggestions={filteredSuggestions}
                    onSelect={handleSuggestionSelect}
                    selectedIndex={selectedSuggestionIndex}
                    position={getDropdownPosition()}
                    onClose={handleDropdownClose}
                />
            )}
        </div>
    );
}

export default React.memo(TestChartEntry, (prev, next) =>
    prev.test === next.test &&
    prev.isSelected === next.isSelected &&
    prev.aggregate === next.aggregate &&
    prev.tags === next.tags
);
